// src/components/Navbar.js
import React, { useState, useEffect, useCallback } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Container,
  Badge,
  Tooltip,
  Paper,
  Divider,
} from "@mui/material";
import { Link } from "react-router-dom";
import MenuIcon from "@mui/icons-material/Menu";
import NotificationsIcon from "@mui/icons-material/Notifications";
import DoneIcon from "@mui/icons-material/Done";
import AccountCircle from "@mui/icons-material/AccountCircle";
import Logo from "./logo_temp.png";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material/styles";
import { CircularProgress } from "@mui/material"; // Import CircularProgress
import LogoutIcon from "@mui/icons-material/Logout";
import { io } from "socket.io-client";
import BidsIcon from "@mui/icons-material/AccountBalanceWallet";
import MyAuctionsIcon from "@mui/icons-material/Gavel";
import AllAuctionsIcon from "@mui/icons-material/Forest";

import {
  getMyNotifications,
  markNotificationAsRead,
} from "../../api/notifications"; // Assuming you'll create this API
import { useAuth } from "../../context/AuthContext";

import Banner from "./Banner";
import DesktopLink from "./DesktopLink";

const Navbar = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [socket, setSocket] = useState(null);
  const { isLoggedIn, logout } = useAuth();
  const [openDrawer, setOpenDrawer] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [initials, setInitials] = useState(null);
  const [submittingMarkAsRead, setSubmittingMarkAsRead] = useState(false);

  const toggleDrawer = (open) => () => {
    setOpenDrawer(open);
  };

  const handleLogout = () => {
    logout();
  };

  const handleNotificationsToggle = () => {
    setNotificationsOpen(!notificationsOpen);
  };

  const handleMarkAsRead = async (notificationId) => {
    setSubmittingMarkAsRead(true);
    try {
      await markNotificationAsRead(notificationId);
      setNotifications((prevNotifications) =>
        prevNotifications.map((n) =>
          n.id === notificationId ? { ...n, is_read: true } : n,
        ),
      );
      setUnreadCount((prevCount) => (prevCount > 0 ? prevCount - 1 : 0));
      setSubmittingMarkAsRead(false);
    } catch (error) {
      console.error("Error marking notification as read:", error);
      setSubmittingMarkAsRead(false);
    }
  };

  const fetchNotifications = useCallback(async () => {
    if (isLoggedIn) {
      try {
        let data = await getMyNotifications();
        data = Array.isArray(data) ? data : [];
        setNotifications(data);
        setUnreadCount(data.filter((n) => !n.is_read).length);
      } catch (error) {
        console.error("Failed to fetch notifications:", error);
      }
    } else {
      setNotifications([]);
      setUnreadCount(0);
    }
  }, [isLoggedIn, getMyNotifications, setNotifications, setUnreadCount]);

  useEffect(() => {
    const newSocket = io(process.env.REACT_APP_API_URL, {
      withCredentials: true,
    });
    setSocket(newSocket);

    return () => {
      newSocket.disconnect();
    };
  }, []);

  useEffect(() => {
    if (socket) {
      socket.on("new_notification", () => {
        fetchNotifications();
      });

      fetchNotifications();

      return () => {
        socket.off("new_notification");
      };
    }
  }, [socket, fetchNotifications]);

  useEffect(() => {
    setInitials(localStorage.getItem("initials"));
  }, [setInitials]);

  // useEffect(async () => {

  //     const fetchNotifications = async () => {
  //         console.log("Is logged in:", isLoggedIn);
  //         if (isLoggedIn) {
  //             try {
  //                 console.log("Fetching notifications for user");
  //                 const data = await getMyNotifications();
  //                 console.log("Fetched notifications:", data);
  //                 setNotifications(data);
  //                 setUnreadCount(data.filter(n => !n.is_read).length);
  //             } catch (error) {
  //                 console.error("Failed to fetch notifications:", error);
  //             }
  //         } else {
  //             setNotifications([]);
  //             setUnreadCount(0);
  //         }
  //     };

  //     const socket = io(process.env.REACT_APP_API_URL);
  //     setSocket(socket);

  //     socket.on("connect", () => {
  //         // console.log("Connected to socket:", socket.id);
  //     });
  //     socket.on("new_notification", () => {
  //         fetchNotifications();
  //     });

  //     fetchNotifications();

  //     setInitials(localStorage.getItem("initials"));

  //     return () => {
  //         socket.disconnect();
  //     };
  // }, []);

  const unreadNotifications = notifications.filter((n) => !n.is_read);

  const mobileNavItems = isLoggedIn
    ? [
        {
          text: (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <MyAuctionsIcon sx={{ mr: 2 }} />
              {t("navigation.my_auctions")}
            </Box>
          ),
          to: "/izsoles",
        },
        {
          text: (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <BidsIcon sx={{ mr: 2 }} />
              {t("navigation.my_bids")}
            </Box>
          ),
          to: "/likmes",
        },
        {
          text: (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <AllAuctionsIcon sx={{ mr: 2 }} />
              {t("navigation.all_auctions")}
            </Box>
          ),
          to: "/visas_izsoles",
        },
        // {
        //     text: (
        //         <Box sx={{ display: 'flex', alignItems: 'center' }}>
        //             <AccountCircle sx={{ mr: 1 }} />
        //             {initials}
        //         </Box>
        //     ),
        //     to: "/profils",
        // },
        // { text: t("navigation.logout"), onClick: handleLogout },
      ]
    : [
        { text: t("navigation.home"), to: "/" },
        { text: t("navigation.sell_forest"), to: "/izsoles" },
        { text: t("navigation.active_auctions"), to: "/visas_izsoles" },
        { text: t("navigation.contacts"), to: "/kontakti" },
        { text: t("navigation.navbar_cta"), to: "/ienakt" },
      ];

  return (
    <>
      <Banner />

      <AppBar
        position="sticky"
        sx={{
          px: 2,
          py: 0.5,
          borderColor: "transparent",
          backgroundColor: "white",
          boxShadow: 0,
          borderRadius: 0,
          boxShadow:
            "0px 2px 30px rgba(0, 0, 0, 0.03), 0px 1px 3px rgba(0, 0, 0, 0.04)",
        }}
      >
        <Container sx={{ mx: 0, width: "100%" }} maxWidth="100">
          <Toolbar sx={{ p: "0 !important" }}>
            <Box sx={{ display: "flex", alignItems: "center", flexGrow: 1 }}>
              <Box
                component={Link}
                to="/"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  textDecoration: "none",
                  color: "inherit",
                }}
              >
                <img
                  src={Logo}
                  alt="Logo"
                  style={{ height: "30px", marginRight: "30px" }}
                />
              </Box>

              {!isLoggedIn ? (
                <>
                  <DesktopLink to="/">{t("navigation.home")}</DesktopLink>
                  <DesktopLink to="/izsoles">
                    {t("navigation.sell_forest")}
                  </DesktopLink>
                  <DesktopLink to="/visas_izsoles">
                    {t("navigation.active_auctions")}
                  </DesktopLink>
                  <DesktopLink to="/kontakti">
                    {t("navigation.contacts")}
                  </DesktopLink>
                </>
              ) : (
                <>
                  <DesktopLink to="/izsoles">
                    {t("navigation.my_auctions")}
                  </DesktopLink>
                  <DesktopLink to="/likmes">
                    {t("navigation.my_bids")}
                  </DesktopLink>
                  <DesktopLink to="/visas_izsoles">
                    {t("navigation.all_auctions")}
                  </DesktopLink>
                </>
              )}
            </Box>

            <IconButton
              edge="end"
              color="black"
              aria-label="menu"
              onClick={toggleDrawer(true)}
              sx={{
                display: { xs: "block", md: "none" },
                color: "primary.main",
                fontSize: "40px",
              }}
            >
              <MenuIcon />
            </IconButton>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                display: { xs: "none", md: "flex" },
              }}
            >
              {isLoggedIn && (
                <Box sx={{ position: "relative", mr: 1 }}>
                  <IconButton
                    color="primary"
                    aria-label="notifications"
                    onClick={handleNotificationsToggle}
                    size="large"
                  >
                    <Badge
                      badgeContent={unreadCount > 0 ? unreadCount : null}
                      color="error"
                    >
                      <NotificationsIcon
                        sx={{ color: "primary", fontSize: "24px" }}
                      />
                    </Badge>
                  </IconButton>
                  {notificationsOpen && unreadNotifications.length > 0 && (
                    <Paper
                      variant="contained"
                      sx={{
                        position: "absolute",
                        top: "100%",
                        right: 0,
                        zIndex: 1,
                        minWidth: 300,
                        maxHeight: "50vh",
                        overflowY: "auto",
                        boxShadow:
                          "0px 2px 30px rgba(0, 0, 0, 0.03), 0px 1px 3px rgba(0, 0, 0, 0.04)",
                        mt: 0.5,
                      }}
                      onMouseLeave={() =>
                        setTimeout(() => setNotificationsOpen(false), 300)
                      } // Optional: close on mouse leave
                    >
                      <List>
                        {unreadNotifications.map((notification) => {
                          const messageKey = `notifications.${notification.message}`;
                          const hasParameter =
                            notification.parameter !== undefined;

                          const message = t(
                            messageKey,
                            hasParameter
                              ? { value: notification.parameter }
                              : {},
                          );
                          return (
                            <ListItem
                              key={notification.id}
                              alignItems="center"
                              sx={{ py: 1 }}
                              secondaryAction={
                                <Tooltip
                                  title={t("notifications.mark_as_read")}
                                >
                                  <IconButton
                                    edge="end"
                                    aria-label="mark as read"
                                    onClick={(e) => {
                                      e.stopPropagation(); // stop event bubbling
                                      e.preventDefault(); // also stop link triggering (safety)
                                      handleMarkAsRead(notification.id);
                                    }}
                                    size="small"
                                  >
                                    {submittingMarkAsRead ? (
                                      <CircularProgress
                                        size={24}
                                        color="inherit"
                                      />
                                    ) : (
                                      <DoneIcon
                                        sx={{
                                          color: theme.palette.success.main,
                                        }}
                                      />
                                    )}
                                  </IconButton>
                                </Tooltip>
                              }
                            >
                              {notification.link ? (
                                <Link
                                  to={notification.link}
                                  // TODO replace with notification.blank when implemented
                                  target={
                                    notification.message?.trim() ===
                                    "new_invoice"
                                      ? "_blank"
                                      : "_self"
                                  }
                                  style={{
                                    textDecoration: "none",
                                    color: "inherit",
                                    flexGrow: 1,
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    color="textSecondary"
                                  >
                                    {message}
                                  </Typography>
                                </Link>
                              ) : (
                                <Typography
                                  variant="body2"
                                  color="textSecondary"
                                >
                                  {message}
                                </Typography>
                              )}
                            </ListItem>
                          );
                        })}
                      </List>
                    </Paper>
                  )}
                  {notificationsOpen && unreadNotifications.length === 0 && (
                    <Paper
                      sx={{
                        position: "absolute",
                        top: "100%",
                        right: 0,
                        zIndex: 1,
                        minWidth: 200,
                        mt: 1,
                        p: 2,
                      }}
                      onMouseLeave={() =>
                        setTimeout(() => setNotificationsOpen(false), 300)
                      } // Optional: close on mouse leave
                    >
                      <Typography variant="body2" color="textSecondary">
                        {t("notifications.no_notifications")}
                      </Typography>
                    </Paper>
                  )}
                </Box>
              )}

              {!isLoggedIn ? (
                <Button
                  component={Link}
                  to="/ienakt"
                  sx={{
                    border: "2px solid",
                    borderColor: theme.palette.primary.main,
                    borderRadius: "50px",
                    fontWeight: "bold",
                    px: 2,
                    // STRIPE SHADOW
                    boxShadow: "0px 0px 0px rgba(0,0,0,0.0)",
                    "&:hover": {
                      // boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.08), 0px 1px 3px rgba(0, 0, 0, 0.05)',
                      backgroundColor: theme.palette.primary.main,
                      color: theme.palette.primary.contrastText,
                    },
                  }}
                >
                  {t("navigation.navbar_cta")}
                </Button>
              ) : (
                <>
                  <Button
                    component={Link}
                    to="/profils"
                    size="extralarge"
                    sx={{
                      mr: 1,
                      px: 2,
                      textTransform: "uppercase",
                      fontSize: "16px",
                    }}
                    startIcon={<AccountCircle />}
                  >
                    {initials}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleLogout}
                    sx={{
                      fontSize: "14px",
                      color: "primary.contrastText",
                      backgroundColor: "primary.main",
                      borderRadius: "50px",
                      "&:hover": {
                        borderColor: theme.palette.secondary.main,
                        backgroundColor: theme.palette.secondary.main,
                      },
                    }}
                  >
                    {t("navigation.logout")}
                  </Button>
                </>
              )}
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="bottom"
        fullWidth
        sx={{ overflow: "hidden" }}
        open={openDrawer}
        onClose={toggleDrawer(false)}
      >
        <Box>
          <List>
            {mobileNavItems.map((item, index) => (
              <>
                <ListItem
                  sx={{ ml: 0 }}
                  button
                  key={index}
                  component={Link}
                  to={item.to}
                  onClick={item.onClick ? item.onClick : toggleDrawer(false)}
                >
                  <ListItemText
                    primary={
                      typeof item.text === "object" ? item.text : t(item.text)
                    }
                    primaryTypographyProps={{
                      fontSize: "18px", // or e.g. '18px'
                      fontWeight: "bold",
                      color: "primary.main",
                    }}
                  />
                </ListItem>
                {/* <Divider sx={{ my: 1 }} /> */}
              </>
            ))}
            {isLoggedIn && (
              <>
                <Divider sx={{ my: 1 }} />
                <ListItem
                  button
                  key={"profile"}
                  component={Link}
                  to="/profils"
                  onClick={toggleDrawer(false)}
                >
                  <ListItemText
                    primary={
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          color: "primary.main",
                        }}
                      >
                        <AccountCircle sx={{ mr: 2, ml: 0 }} />
                        {t("navigation.my_account")}
                      </Box>
                    }
                    primaryTypographyProps={{
                      fontSize: "18px", // or e.g. '18px'
                      fontWeight: "bold",
                    }}
                  />
                </ListItem>

                <Divider sx={{ my: 1 }} />

                <ListItem>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        py: 0.5,
                        color: "primary.main",
                      }}
                      color="inherit"
                      aria-label="notifications"
                      onClick={handleNotificationsToggle}
                      fontSize={"18px"}
                      fontWeight={"bold"}
                    >
                      <Badge
                        sx={{ mr: 2.5, ml: 0 }}
                        badgeContent={unreadCount > 0 ? unreadCount : null}
                        color="error"
                      >
                        <NotificationsIcon
                          sx={{ color: "primary.main", fontSize: 24 }}
                        />
                      </Badge>
                      {t("navigation.notifications")}
                    </Box>
                    {notificationsOpen && unreadNotifications.length > 0 && (
                      <Paper elevation={3} sx={{ mt: 1 }}>
                        <List>
                          {unreadNotifications.map((notification) => (
                            <ListItem
                              key={notification.id}
                              alignItems="center"
                              sx={{ py: 1 }}
                            >
                              {/* TODO don't do this inline, for clarity sake */}
                              <Typography variant="body2" color="textSecondary">
                                {t(
                                  `notifications.${notification.message}`,
                                  notification.parameter !== undefined
                                    ? { value: notification.parameter }
                                    : {},
                                )}
                              </Typography>
                              <Tooltip title={t("notifications.mark_as_read")}>
                                <IconButton
                                  edge="end"
                                  aria-label="mark as read"
                                  onClick={() =>
                                    handleMarkAsRead(notification.id)
                                  }
                                  size="small"
                                >
                                  {submittingMarkAsRead ? (
                                    <CircularProgress
                                      size={24}
                                      color="inherit"
                                    /> // Show spinner when loading
                                  ) : (
                                    <DoneIcon
                                      sx={{ color: theme.palette.success.main }}
                                    />
                                  )}
                                </IconButton>
                              </Tooltip>
                            </ListItem>
                          ))}
                        </List>
                      </Paper>
                    )}
                    {notificationsOpen && unreadNotifications.length === 0 && (
                      <Paper sx={{ mt: 1, p: 2 }}>
                        <Typography variant="body2" color="textSecondary">
                          {t("notifications.no_notifications")}
                        </Typography>
                      </Paper>
                    )}
                  </Box>
                </ListItem>

                <Divider sx={{ my: 1 }} />

                <ListItem
                  sx={{ color: "primary.main" }}
                  button
                  key={"logout"}
                  component={Link}
                  onClick={handleLogout}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <LogoutIcon sx={{ mr: 2, ml: 0 }} />
                        {t("navigation.logout")}
                      </Box>
                    }
                    primaryTypographyProps={{
                      fontSize: "18px", // or e.g. '18px'
                      fontWeight: "bold",
                    }}
                  />
                </ListItem>
              </>
            )}
          </List>
        </Box>
      </Drawer>
    </>
  );
};

export default Navbar;
